import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  buildStoredPreference,
  normalizeDeviceOptions,
  resolveMatchingDevice,
} from './mediaDeviceSelectorUtils';

const isBrowserMediaDevicesSupported = () =>
  typeof navigator !== 'undefined' &&
  Boolean(navigator.mediaDevices?.enumerateDevices) &&
  Boolean(navigator.mediaDevices?.getUserMedia);

const MediaDeviceSelectPill = ({
  kind,
  fallbackId,
  fallbackLabel,
  pillLabel,
  Icon,
  isReady = true,
  onMediaNotice,
  onSelectDevice,
  listDevices,
  readPreference,
  persistPreference,
  requestPermissions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isSwitchingDevice, setIsSwitchingDevice] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [devices, setDevices] = useState([]);
  const [selectedPreference, setSelectedPreference] = useState(() => readPreference());

  const containerRef = useRef(null);
  const hasRequestedPermissionsRef = useRef(false);
  const browserMediaDevicesSupported = isBrowserMediaDevicesSupported();
  const mediaDevicesSupported = Boolean(listDevices) || browserMediaDevicesSupported;

  const notifyMediaNotice = useCallback((message) => {
    if (!message || typeof onMediaNotice !== 'function') return;
    onMediaNotice(message);
  }, [onMediaNotice]);

  const warmPermission = useCallback(async () => {
    if (typeof requestPermissions === 'function') {
      await requestPermissions();
      hasRequestedPermissionsRef.current = true;
      return;
    }

    if (!browserMediaDevicesSupported) {
      throw new Error('Media device selection is not supported in this browser.');
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    stream.getTracks().forEach((track) => track.stop());
    hasRequestedPermissionsRef.current = true;
  }, [browserMediaDevicesSupported, requestPermissions]);

  const refreshDevices = useCallback(async ({ ensurePermission = false } = {}) => {
    if (!mediaDevicesSupported) {
      setDevices([]);
      setDeviceError('Media device selection is not supported in this browser.');
      return [];
    }

    setIsLoadingDevices(true);
    try {
      if (ensurePermission) {
        await warmPermission();
      }

      const availableDevices = typeof listDevices === 'function'
        ? await listDevices()
        : await navigator.mediaDevices.enumerateDevices();
      const nextDevices = normalizeDeviceOptions({
        devices: availableDevices,
        kind,
        fallbackId,
        fallbackLabel,
      });
      setDevices(nextDevices);
      setDeviceError('');
      return nextDevices;
    } catch (error) {
      const nextMessage = 'Media device access is blocked. Grant browser permission and try again.';
      setDeviceError(nextMessage);
      notifyMediaNotice(nextMessage);
      return [];
    } finally {
      setIsLoadingDevices(false);
    }
  }, [fallbackId, fallbackLabel, kind, listDevices, mediaDevicesSupported, notifyMediaNotice, warmPermission]);

  useEffect(() => {
    refreshDevices();

    if (!mediaDevicesSupported || !navigator.mediaDevices) return undefined;

    const handleDeviceChange = () => {
      refreshDevices({ ensurePermission: hasRequestedPermissionsRef.current });
    };

    const handleWindowFocus = () => {
      refreshDevices({ ensurePermission: hasRequestedPermissionsRef.current });
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshDevices({ ensurePermission: hasRequestedPermissionsRef.current });
      }
    };

    navigator.mediaDevices.addEventListener?.('devicechange', handleDeviceChange);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      navigator.mediaDevices.removeEventListener?.('devicechange', handleDeviceChange);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [mediaDevicesSupported, refreshDevices]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!devices.length) return;

    const resolvedDevice =
      resolveMatchingDevice(devices, selectedPreference, fallbackId) ||
      devices.find((device) => device.deviceId === fallbackId) ||
      devices[0];

    if (!resolvedDevice) return;

    const resolvedPreference = buildStoredPreference(resolvedDevice, fallbackId, fallbackLabel);
    if (resolvedPreference.selectionId !== selectedPreference.selectionId) {
      persistPreference(resolvedPreference);
      setSelectedPreference(resolvedPreference);
    }
  }, [devices, fallbackId, fallbackLabel, persistPreference, selectedPreference]);

  const activeSelectedDevice = useMemo(
    () => resolveMatchingDevice(devices, selectedPreference, fallbackId),
    [devices, fallbackId, selectedPreference]
  );

  const selectedLabel = activeSelectedDevice?.displayLabel ||
    selectedPreference?.label ||
    fallbackLabel;

  const handleToggleMenu = async () => {
    if (!isReady || !mediaDevicesSupported) return;

    if (!isOpen) {
      await refreshDevices({ ensurePermission: true });
    }

    setIsOpen((previous) => !previous);
  };

  const handleSelectDevice = async (deviceOption) => {
    if (!isReady || isSwitchingDevice || typeof onSelectDevice !== 'function') return;

    if (deviceOption.selectionId === activeSelectedDevice?.selectionId) {
      setIsOpen(false);
      return;
    }

    setIsSwitchingDevice(true);
    try {
      const didSwitch = await onSelectDevice(deviceOption);

      if (didSwitch === false) {
        throw new Error('Device switch failed.');
      }

      const refreshedDevices = await refreshDevices({
        ensurePermission: hasRequestedPermissionsRef.current,
      });
      const resolvedDevice =
        resolveMatchingDevice(refreshedDevices, deviceOption, fallbackId) ||
        resolveMatchingDevice(refreshedDevices, selectedPreference, fallbackId) ||
        deviceOption;
      const nextPreference = buildStoredPreference(resolvedDevice, fallbackId, fallbackLabel);

      persistPreference(nextPreference);
      setSelectedPreference(nextPreference);
      setDeviceError('');
      setIsOpen(false);
    } catch (error) {
      const nextMessage = `Unable to switch ${pillLabel.toLowerCase()}. The current device is still active.`;
      setDeviceError(nextMessage);
      notifyMediaNotice(nextMessage);
    } finally {
      setIsSwitchingDevice(false);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={handleToggleMenu}
        disabled={!isReady || !mediaDevicesSupported}
        title={`${pillLabel}: ${selectedLabel}`}
        className={`flex h-8 w-full items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 text-left text-white transition-colors ${(!isReady || !mediaDevicesSupported) ? 'cursor-not-allowed opacity-50' : 'hover:bg-white/10'}`}
      >
        <Icon sx={{ fontSize: 20, color: 'currentColor' }} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {pillLabel} ({selectedLabel})
        </span>
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: 'currentColor' }} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/95 shadow-[0_20px_50px_rgba(2,6,23,0.55)] backdrop-blur-xl">
          <div className="max-h-72 overflow-y-auto py-2">
            {isLoadingDevices ? (
              <div className="px-4 py-6 text-sm text-slate-300">Loading {pillLabel.toLowerCase()}…</div>
            ) : devices.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-300">No {pillLabel.toLowerCase()} devices found.</div>
            ) : (
              devices.map((device) => {
                const isSelected = device.selectionId === activeSelectedDevice?.selectionId;

                return (
                  <button
                    key={device.optionKey}
                    type="button"
                    onClick={() => handleSelectDevice(device)}
                    disabled={isSwitchingDevice}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? 'bg-emerald-500/10 text-emerald-200'
                        : 'text-slate-100 hover:bg-white/5'
                    } ${isSwitchingDevice ? 'cursor-wait opacity-70' : ''}`}
                  >
                    <Icon sx={{ fontSize: 18, color: 'currentColor' }} />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {device.displayLabel}
                    </span>
                    {isSelected && <CheckRoundedIcon sx={{ fontSize: 18, color: 'currentColor' }} />}
                  </button>
                );
              })
            )}
          </div>

          {(deviceError || isSwitchingDevice) && (
            <div className="border-t border-white/10 px-4 py-3 text-xs text-slate-300">
              {isSwitchingDevice ? `Switching ${pillLabel.toLowerCase()}…` : deviceError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaDeviceSelectPill;
